export function useUtils() {
	function debounce(fn, delay) {
		// If delay is 0, execute immediately without timer overhead
		if (delay === 0) {
			return function (...args) {
				fn(...args);
			};
		}

		let timer;
		return function (...args) {
			clearTimeout(timer);
			timer = setTimeout(() => {
				fn(...args);
			}, delay);
		};
	}

	async function sha256(input) {
		if (!input || typeof input !== 'string') {
			if (typeof input === 'number') {
				input = input.toString();
			} else {
				return null;
			}
		}

		const cleanedInput = input.trim().toLowerCase();
		const encoder = new TextEncoder();
		const data = encoder.encode(cleanedInput);
		const hashBuffer = await crypto.subtle.digest('SHA-256', data);
		return Array.from(new Uint8Array(hashBuffer))
			.map(byte => byte.toString(16).padStart(2, '0'))
			.join('');
	}

	async function detectCountryByIP() {
		try {
			const response = await useApi('https://api.country.is/');
			if (response?.country) {
				return response.country.toLowerCase();
			}
		} catch (error) {
			console.error('IP country detection failed:', error);
		}
		return null;
	}

	function detectMobile() {
		// Detects if the current request/client is a mobile device.
		// - SSR: checks the request user-agent header
		// - Client: checks navigator.userAgent and falls back to media queries
		const uaRegex = /(android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini)/i;

		if (import.meta.server) {
			try {
				const headers = useRequestHeaders(['user-agent']);
				const ua = headers?.['user-agent'] || '';
				return uaRegex.test(ua);
			} catch (e) {
				return false;
			}
		}

		// Client-side checks
		try {
			const nav = typeof navigator !== 'undefined' ? navigator : null;
			const w = typeof window !== 'undefined' ? window : null;
			const ua = (nav && (nav.userAgent || nav.vendor)) || '';
			if (ua && uaRegex.test(ua)) return true;

			if (w && w.matchMedia) {
				if (w.matchMedia('(pointer: coarse)').matches) return true;
				if (w.matchMedia('(max-width: 767px)').matches) return true;
			}
		} catch (e) {
			// no-op
		}

		return false;
	}

	const userInitialized = useState('userInitialized', () => false);
	let listenersAttached = false;
	let handleUserEngagement, removeListeners;
	let timeoutId = null;

	function initializeUserInteraction() {
		if (import.meta.server) return;

		// If the user has already started interacting, there's no need for listeners
		if (userInitialized.value) return;

		// Check if user has already interacted (scroll position indicates interaction)
		if (window.scrollY > 50 || document.documentElement.scrollTop > 50) {
			userInitialized.value = true;
			return;
		}

		// Define functions if not already defined
		if (!handleUserEngagement) {
			handleUserEngagement = function () {
				userInitialized.value = true;
				removeListeners();
				if (timeoutId) {
					clearTimeout(timeoutId);
					timeoutId = null;
				}
			};

			removeListeners = function () {
				if (!listenersAttached) return;

				document.removeEventListener('pointermove', handleUserEngagement, true);
				document.removeEventListener('wheel', handleUserEngagement, true);
				document.removeEventListener('touchstart', handleUserEngagement, true);
				document.removeEventListener('touchmove', handleUserEngagement, true);
				document.removeEventListener('keydown', handleUserEngagement, true);
				document.removeEventListener('click', handleUserEngagement, true);
				document.removeEventListener('scroll', handleUserEngagement, true);

				listenersAttached = false;
			};
		}

		// Attach listeners to document with capture phase to catch events early
		if (!listenersAttached) {
			document.addEventListener('pointermove', handleUserEngagement, {passive: true, capture: true});
			document.addEventListener('wheel', handleUserEngagement, {passive: true, capture: true});
			document.addEventListener('touchstart', handleUserEngagement, {passive: true, capture: true});
			document.addEventListener('touchmove', handleUserEngagement, {passive: true, capture: true});
			document.addEventListener('keydown', handleUserEngagement, {capture: true});
			document.addEventListener('click', handleUserEngagement, {capture: true});
			document.addEventListener('scroll', handleUserEngagement, {passive: true, capture: true});

			listenersAttached = true;
		}

		// Auto-initialize after window load + 5 seconds if no user interaction
		if (!timeoutId) {
			timeoutId = setTimeout(() => {
				if (!userInitialized.value) {
					userInitialized.value = true;
					removeListeners();
				}
				timeoutId = null;
			}, 8000);
		}
	}

	/**
	 * Generate QR code using multiple services with fallback
	 * @param {Object} options - Options for QR code generation
	 * @param {string} options.data - Data for QR code
	 * @param {number} options.size - Size of QR code (default: 150)
	 * @returns {Promise<string|null>} - URL of QR code image or null if no service is available
	 */
	async function generateQrCode(options) {
		if (import.meta.server) return null;
		if (!options.data) {
			console.error('No data provided for QR code generation');
			return null;
		}
		const encodedData = encodeURIComponent(options.data);
		const size = options?.size || 150;

		const qrServices = [
			`https://quickchart.io/qr?text=${encodedData}&size=${size}`,
			`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodedData}`,
			`https://image-charts.com/chart?chs=${size}x${size}&cht=qr&choe=UTF-8&chl=${encodedData}`,
			`https://api.goqr.me/qr/create?size=${size}x${size}&data=${encodedData}`,
		];

		function checkImageUrl(url) {
			return new Promise(resolve => {
				const img = new Image();
				const timer = setTimeout(() => resolve(false), 3000);

				img.onload = () => {
					clearTimeout(timer);
					resolve(img.width > 50 && img.height > 50);
				};

				img.onerror = () => {
					clearTimeout(timer);
					resolve(false);
				};

				img.src = url;
			});
		}

		// Try each service in sequence
		for (let i = 0; i < qrServices.length; i++) {
			try {
				const url = qrServices[i];
				const isValid = await checkImageUrl(url);

				if (isValid) {
					return url;
				}
			} catch (error) {
				continue;
			}
		}

		return null;
	}

	return {debounce, sha256, detectCountryByIP, detectMobile, initializeUserInteraction, userInitialized, generateQrCode};
}
